import React, { useState, useEffect, useCallback } from 'react';
import {
  Star,
  Search,
  Eye,
  EyeOff,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Filter,
  User,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import reviewService from '../../services/reviewService';
import { fuzzyMatch } from '../../lib/fuzzySearch';

const ReviewManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReviews, setTotalReviews] = useState(0);
  const [approvedFilter, setApprovedFilter] = useState(''); // '' | 'true' | 'false'
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const REVIEWS_PER_PAGE = 15;

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: REVIEWS_PER_PAGE };
      if (approvedFilter !== '') params.approved = approvedFilter;
      const result = await reviewService.adminGetAllReviews(params);
      if (result.success) {
        setReviews(result.data.reviews || []);
        const pagination = result.data.pagination || {};
        setTotalPages(pagination.pages || 1);
        setTotalReviews(pagination.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch reviews', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, approvedFilter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    setCurrentPage(1);
  }, [approvedFilter]);

  const handleToggleApproval = async (review) => {
    setActionLoading(review.id);
    const result = await reviewService.adminToggleApproval(review.id, !review.isApproved);
    if (result.success) {
      setReviews((prev) =>
        prev.map((r) => (r.id === review.id ? { ...r, isApproved: !r.isApproved } : r))
      );
    }
    setActionLoading(null);
  };

  const handleDelete = async (reviewId) => {
    setActionLoading(reviewId);
    const result = await reviewService.adminDeleteReview(reviewId);
    if (result.success) {
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      setTotalReviews((prev) => prev - 1);
    }
    setDeleteConfirm(null);
    setActionLoading(null);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  };

  const filteredReviews = searchQuery
    ? reviews.filter(
        (r) =>
          fuzzyMatch(searchQuery, r.user?.name || '') ||
          fuzzyMatch(searchQuery, r.product?.name || '') ||
          fuzzyMatch(searchQuery, r.title || '') ||
          fuzzyMatch(searchQuery, r.comment || '')
      )
    : reviews;

  const renderStars = (rating) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`w-3.5 h-3.5 ${s <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Review Management</h1>
          <p className="text-gray-500 text-sm">{totalReviews} total reviews</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user, product, title, or comment..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={approvedFilter}
              onChange={(e) => setApprovedFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
            >
              <option value="">All Reviews</option>
              <option value="true">Visible</option>
              <option value="false">Hidden</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reviews Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading reviews...</div>
        ) : filteredReviews.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No reviews found</div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Rating</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Review</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredReviews.map((review) => (
                    <tr key={review.id} className="hover:bg-gray-50 transition-colors">
                      {/* User */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{review.user?.name || 'Unknown'}</p>
                            {review.isVerifiedPurchase && (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600">
                                <ShieldCheck className="w-3 h-3" /> Verified
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Product */}
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700 truncate max-w-[180px]">{review.product?.name || '—'}</p>
                      </td>
                      {/* Rating */}
                      <td className="px-4 py-3">{renderStars(review.rating)}</td>
                      {/* Review */}
                      <td className="px-4 py-3">
                        <div className="max-w-[250px]">
                          {review.title && <p className="text-sm font-medium text-gray-800 truncate">{review.title}</p>}
                          {review.comment && (
                            <p className="text-xs text-gray-500 truncate">{review.comment}</p>
                          )}
                        </div>
                      </td>
                      {/* Date */}
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-500">{formatDate(review.createdAt)}</p>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                            review.isApproved
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {review.isApproved ? (
                            <>
                              <Eye className="w-3 h-3" /> Visible
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-3 h-3" /> Hidden
                            </>
                          )}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleToggleApproval(review)}
                            disabled={actionLoading === review.id}
                            className={`p-2 rounded-lg transition-colors text-sm ${
                              review.isApproved
                                ? 'text-orange-600 hover:bg-orange-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={review.isApproved ? 'Hide review' : 'Show review'}
                          >
                            {review.isApproved ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(review)}
                            disabled={actionLoading === review.id}
                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete review"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-100">
              {filteredReviews.map((review) => (
                <div key={review.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{review.user?.name || 'Unknown'}</p>
                        {review.isVerifiedPurchase && (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600">
                            <ShieldCheck className="w-3 h-3" /> Verified
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        review.isApproved
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {review.isApproved ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">{review.product?.name}</p>
                  <div className="mb-1">{renderStars(review.rating)}</div>
                  {review.title && <p className="text-sm font-medium text-gray-800">{review.title}</p>}
                  {review.comment && <p className="text-xs text-gray-500 line-clamp-2">{review.comment}</p>}
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-gray-400">{formatDate(review.createdAt)}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleApproval(review)}
                        disabled={actionLoading === review.id}
                        className={`p-1.5 rounded-lg text-sm ${
                          review.isApproved
                            ? 'text-orange-600 hover:bg-orange-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {review.isApproved ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(review)}
                        disabled={actionLoading === review.id}
                        className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Delete Review</h3>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              Are you sure you want to permanently delete this review by{' '}
              <span className="font-medium">{deleteConfirm.user?.name}</span>?
            </p>
            {deleteConfirm.title && (
              <p className="text-sm text-gray-500 italic mb-4">"{deleteConfirm.title}"</p>
            )}
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={actionLoading === deleteConfirm.id}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading === deleteConfirm.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewManagement;